import { useSelector } from "react-redux";
import Grid from "@material-ui/core/Grid";
import NoMapContainer from "../NoMapContainer";
import MapModel from "../../../models/MapModel";
import { useDimensions } from "../../../hooks/dimensions.hook";
import { getMapUrl } from "../../../helpers/mapHelpers";
import ZoomMap from "../../../components/Map/ZoomMap";
import DeviceModel from "../../../models/DeviceModel";
import { selectCurrentDeviceId } from "../../../store/deviceStore/deviceSelectors";
import { selectEditMode } from "../../../store/generalStore/generalSelectors";
import { getGeoCoordinatesFromPoint, upsertDevicePoint } from "../../../store/deviceStore/deviceRequests";
import { useTypedDispatch } from "../../../app/state";
import { POINT_EDIT_TYPE, setEditMode } from "../../../store/generalStore/generalState";
import { selectInfluenceAreaEnabled } from "../../../store/filterStore/filterSelectors";
import FilterChipContainer from "../../Shared/FilterChipContainer";
import MapDrawer from "../MapDrawer";
import LocationMapStructureInfluenceArea from "../LocationMapStructureInfluenceArea";
import useFilter from "../../../hooks/filter.hook";
import { setCoordinatesFilter } from "../../../store/filterStore/filterState";
import FilterArea from "../FilterArea";
import MapLegend from "../MapLegend";

import classes from './index.module.scss';

interface DeviceMapProps {
  map?: MapModel,
  deviceList?: DeviceModel[],
}

const DeviceMapContainer = ({ map, deviceList = [] }: DeviceMapProps) => {
  const dispatch = useTypedDispatch();
  const container = useDimensions();
  const { state } = useFilter();
  const { isLocation } = state;
  const currentDeviceId = useSelector(selectCurrentDeviceId);
  const isMapEditMode = useSelector(selectEditMode);
  const isPointMapEdit = isMapEditMode === POINT_EDIT_TYPE.POINT;
  const isFilterPointEdit = isMapEditMode === POINT_EDIT_TYPE.FILTER;
  const influenceAreaEnabled = useSelector(selectInfluenceAreaEnabled);
  const mapFileName = map?.url
  const mapUrl = mapFileName ? getMapUrl(mapFileName) : '';
  const isMapUploaded = !!mapUrl;

  const onClick = (x, y) => {
    if (isPointMapEdit && currentDeviceId) {
      dispatch(upsertDevicePoint({ point: { x, y }, deviceId: currentDeviceId }))
        .then(() => {
          // disable the next device selection
          dispatch(setEditMode(false));
        })
      return;
    }

    if (isFilterPointEdit && map?.id) {
      dispatch(
        getGeoCoordinatesFromPoint({
          x,
          y,
          mapId: map?.id,
        })
      )
        .then((action) => {
          // @ts-ignore
          if (action.payload.error) {
            return;
          }
          const coordinates = action.payload as { latitude: number, longitude: number };

          dispatch(setCoordinatesFilter({
            ...coordinates,
            x,
            y,
          }))
          dispatch(setEditMode(false));
        })
    }
  }

  const filterChipList = [
    FilterChipContainer.FILTER_CHIP_NAMES.INFLUENCE_AREA,
    FilterChipContainer.FILTER_CHIP_NAMES.COVERAGE_PARAMETERS,
    FilterChipContainer.FILTER_CHIP_NAMES.FILTERS,
  ];

  if (isLocation) {
    filterChipList.push(FilterChipContainer.FILTER_CHIP_NAMES.SHOW_INDOOR_DEVICES)
  }

  return (
    <Grid
      container
      item
      xs
      className={classes.gridContainer}
      direction='column'
      innerRef={container.elementRef}
    >
      <FilterChipContainer
        chips={filterChipList}
      />
      {
        !isMapUploaded && (
          <NoMapContainer title='No map has been uploaded' />
        )
      }
      {
        isMapUploaded && (
          <FilterArea>
            <LocationMapStructureInfluenceArea
              influenceAreaEnabled={influenceAreaEnabled}
              deviceList={deviceList}
              currentDeviceId={currentDeviceId}
              mapId={map?.id}
            >
              <MapDrawer>
                <MapLegend>
                  <ZoomMap
                    dimensions={container.dimensions}
                    mapUrl={mapUrl}
                    cursor={isMapEditMode ? 'crosshair' : undefined}
                    onClick={[ onClick ]}
                  />
                </MapLegend>
              </MapDrawer>
            </LocationMapStructureInfluenceArea>
          </FilterArea>
        )
      }
    </Grid>
  )
}

export default DeviceMapContainer;